import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

export const fmtDuration = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};
export const fmtHM = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h} hr ${m} min` : `${m} min`;
};
export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—");
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export const EVENT_LABELS = {
  en_route: "Provider en route", arrival_detected: "GPS arrival detected", check_in: "Checked in", work_start: "Work started",
  pause: "Work paused", resume: "Work resumed", supply_leave: "Left for supplies", supply_return: "Returned to location",
  before_proof: "Before photo added", progress_proof: "Progress proof added", after_proof: "After photo added",
  work_complete: "Work completed", customer_approved: "Customer approved", check_out: "Checked out", departure_detected: "Departure detected",
  visit_ended: "Visit ended", team_check_in: "Team member checked in", team_check_out: "Team member checked out", pin_verified: "Customer PIN verified",
  time_adjustment_requested: "Time adjustment requested", time_adjustment_approved: "Time adjustment approved", time_adjustment_rejected: "Time adjustment rejected",
  time_dispute_opened: "Service time disputed", time_dispute_resolved: "Time dispute resolved", problem_reported: "Problem reported",
  portfolio_permission: "Portfolio permission decided", service_location_set: "Service location pinned",
};

export const VISIT_STATUS_LABEL = {
  scheduled: "Booking confirmed", en_route: "Provider is on the way", arrived: "Provider arrived", checked_in: "Checked in",
  working: "Work in progress", paused: "Work paused", supply_run: "Provider stepped out for supplies", completed: "Work completed — awaiting your confirmation",
  checked_out: "Provider checked out", departed: "Provider left location", closed: "Visit ended",
};

/** Live-updating tick (1s) so timers animate. */
export function useTick(active) {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setN((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
}

export function liveSummary(visit) {
  if (!visit) return { working_seconds: 0, paused_seconds: 0, on_site_seconds: 0, supply_seconds: 0 };
  const now = Date.now();
  const span = (a, b) => (a ? Math.max(0, ((b ? new Date(b) : new Date(now)) - new Date(a)) / 1000) : 0);
  const paused = (visit.pauses || []).reduce((acc, p) => acc + span(p.start, p.end), 0);
  const supply = (visit.supply_runs || []).reduce((acc, p) => acc + span(p.start, p.end), 0);
  const working = visit.work_started_at ? Math.max(0, span(visit.work_started_at, visit.work_completed_at) - paused - supply) : 0;
  const on_site = visit.check_in_at ? span(visit.check_in_at, visit.check_out_at) : 0;
  return { working_seconds: working, paused_seconds: paused, on_site_seconds: on_site, supply_seconds: supply };
}

/** Geolocation, requested only while the caller says the job is active (privacy). */
export function useGeo(active) {
  const [pos, setPos] = useState(null);
  const [permission, setPermission] = useState("unknown");
  useEffect(() => {
    if (!active || typeof navigator === "undefined" || !navigator.geolocation) { if (active) setPermission("unavailable"); return undefined; }
    const id = navigator.geolocation.watchPosition(
      (p) => { setPermission("granted"); setPos({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: Math.round(p.coords.accuracy || 0) }); },
      (e) => setPermission(e.code === 1 ? "denied" : "unavailable"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [active]);
  return { pos, permission, device: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 120) : null };
}

const QKEY = (id) => `gcw_job_queue_${id}`;
export const readQueue = (id) => { try { return JSON.parse(localStorage.getItem(QKEY(id)) || "[]"); } catch { return []; } };
const writeQueue = (id, q) => localStorage.setItem(QKEY(id), JSON.stringify(q));

/** Posts a job action; when offline (network error) queues it for later sync. */
export function useJobActions(bookingId, onChange) {
  const [queued, setQueued] = useState(() => readQueue(bookingId).length);
  const flushing = useRef(false);

  const flush = useCallback(async () => {
    const q = readQueue(bookingId);
    if (!q.length || flushing.current) return;
    flushing.current = true;
    try {
      await api.post(`/jobs/${bookingId}/events/sync`, q);
      writeQueue(bookingId, []);
      setQueued(0);
      onChange && onChange();
    } catch (_) { /* still offline */ } finally { flushing.current = false; }
  }, [bookingId, onChange]);

  useEffect(() => {
    window.addEventListener("online", flush);
    flush();
    return () => window.removeEventListener("online", flush);
  }, [flush]);

  const act = useCallback(async (action, payload = {}) => {
    const PATHS = { en_route: "en-route", check_in: "check-in", start_work: "start-work", pause: "pause", resume: "resume", check_out: "check-out", complete: "complete", proof: "proof", supply_start: "supply-run/start", supply_end: "supply-run/end", end_visit: "end-visit" };
    try {
      const { data } = await api.post(`/jobs/${bookingId}/${PATHS[action]}`, payload);
      onChange && onChange();
      return data;
    } catch (err) {
      if (!err.response) {
        const q = readQueue(bookingId);
        q.push({ client_event_id: `${action}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, action, client_at: new Date().toISOString(), payload });
        writeQueue(bookingId, q);
        setQueued(q.length);
        return { queued: true };
      }
      throw err;
    }
  }, [bookingId, onChange]);

  return { act, queued, flush };
}

export async function uploadJobFile(file, kind = "job_proof", onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: (e) => onProgress && onProgress(Math.round((e.loaded / (e.total || 1)) * 100)) });
  return data;
}

export const fileUrl = (url) => {
  if (!url || !url.startsWith("/api/")) return url;
  const t = localStorage.getItem("gcw_bearer");
  return `${process.env.REACT_APP_BACKEND_URL}${url}${t ? `?auth=${t}` : ""}`;
};
